import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { CloudOff, RefreshCw } from "lucide-react";

import { ENV } from "@/lib/env";
import { getApiHealth, subscribeApiHealth } from "@/lib/api";

/**
 * Banner global que aparece cuando el backend (Railway) es inalcanzable.
 *
 * - Escucha el estado de salud que el cliente HTTP marca en cada request.
 * - Mientras está offline, hace ping a /health cada 10s.
 * - Cuando el ping responde, marca online, oculta el banner e invalida las
 *   queries para que la app se recargue sola sin que el usuario haga nada.
 */
export function ApiStatusBanner() {
  const health = useSyncExternalStore(subscribeApiHealth, getApiHealth, () => "online");
  const qc = useQueryClient();
  const [checking, setChecking] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  async function ping(): Promise<boolean> {
    try {
      const ctrl = new AbortController();
      const t = setTimeout(() => ctrl.abort(), 8000);
      const res = await fetch(`${ENV.API_BASE_URL}/health`, {
        method: "GET",
        signal: ctrl.signal,
        headers: { Accept: "application/json" },
      }).finally(() => clearTimeout(t));
      return res.ok;
    } catch {
      return false;
    }
  }

  async function recover() {
    setChecking(true);
    const ok = await ping();
    setChecking(false);
    if (ok) {
      // Forzamos a TanStack Query a re-pedir todo lo que estaba en error.
      await qc.invalidateQueries();
    }
  }

  // Poll automático mientras estamos caídos.
  useEffect(() => {
    if (health !== "offline") {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return;
    }
    pollRef.current = setInterval(() => {
      void recover();
    }, 10_000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [health]);

  if (health !== "offline") return null;

  return (
    <div
      role="alert"
      className="flex items-center gap-3 border-b border-warn/30 bg-warn-tint px-4 py-2.5 text-sm text-warn"
    >
      <CloudOff className="h-4 w-4 shrink-0" />
      <p className="flex-1 leading-snug">
        <span className="font-semibold">Estamos teniendo problemas de conexión.</span>{" "}
        <span className="text-warn/90">
          Tu información está a salvo — reconectamos en cuanto el servicio
          vuelva. No necesitas hacer nada.
        </span>
      </p>
      <button
        type="button"
        onClick={() => void recover()}
        disabled={checking}
        className="flex shrink-0 items-center gap-1.5 rounded-lg border border-warn/40 px-2.5 py-1 text-xs font-semibold text-warn transition hover:bg-warn/10 disabled:opacity-50"
      >
        <RefreshCw className={checking ? "h-3.5 w-3.5 animate-spin" : "h-3.5 w-3.5"} />
        {checking ? "Reintentando…" : "Reintentar"}
      </button>
    </div>
  );
}
